'use client';

import { motion } from 'motion/react';

import { CardEntrance } from './card-entrance';
import { MobileCard } from './mobile-card';
import {
  FAN_ANGLE_MOBILE,
  FAN_RADIUS_MOBILE,
  INTERACTIVE_OFFSET_MOBILE,
  SLIDE_TRANSITION,
} from './constants';
import {
  entranceSlot,
  fanTransform,
  productAt,
  windowIndices,
} from './geometry';
import { useEntranceReveal } from './useEntranceReveal.hook';
import { usePrevious } from './usePrevious.hook';
import { useSwipe } from './useSwipe.hook';

// 모바일 캐러셀. 스와이프 입력은 useSwipe 훅이 담당하고, 여기선 원형(dome) 배치 렌더링에 집중한다.
export function MobileCarousel({
  active,
  goTo,
  entered,
}: {
  active: number;
  goTo: (i: number) => void;
  // 스크롤 진입으로 카드 등장을 시작할지
  entered: boolean;
}) {
  const prevActive = usePrevious(active);
  const swipe = useSwipe({ active, goTo });
  // 최초 등장 대상 카드 집합(마운트 시점 캡처) — 이후 이동으로 들어온 카드는 등장 제외
  const shouldRunEntrance = useEntranceReveal(
    active,
    INTERACTIVE_OFFSET_MOBILE,
  );

  return (
    <motion.div
      className="relative h-[448px] w-full"
      // 좌우(양옆 카드)만 자르고, 상하는 카드 그림자·아크로 내려간 이웃 카드가 보이도록 살린다
      style={{ clipPath: 'inset(-40px 0px -60px 0px)' }}
      {...swipe}
    >
      {windowIndices(active).map(j => {
        const offset = j - active;
        // 데스크탑과 동일한 원형(dome) 배치 — 모바일 반경·각도로
        const init = fanTransform(
          j - prevActive,
          FAN_RADIUS_MOBILE,
          FAN_ANGLE_MOBILE,
        );
        const cur = fanTransform(offset, FAN_RADIUS_MOBILE, FAN_ANGLE_MOBILE);
        const interactive = Math.abs(offset) <= INTERACTIVE_OFFSET_MOBILE;
        return (
          <motion.div
            key={j}
            className="absolute left-1/2 top-0 -ml-[140px]"
            style={{
              zIndex: cur.zIndex,
              pointerEvents: interactive ? 'auto' : 'none',
            }}
            initial={{ x: init.x, y: init.y, rotate: init.rotate }}
            animate={{ x: cur.x, y: cur.y, rotate: cur.rotate }}
            transition={SLIDE_TRANSITION}
          >
            <CardEntrance
              run={shouldRunEntrance(j)}
              entered={entered}
              slot={entranceSlot(offset, INTERACTIVE_OFFSET_MOBILE)}
            >
              <MobileCard product={productAt(j)} interactive={interactive} />
            </CardEntrance>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
